import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Folder as FolderIcon } from "@mui/icons-material";
import { TABLE_CONFIG_2 } from "../../../constants/patrolFeatureTab";
import toast from "react-hot-toast";
import { detectFileType } from "../../../utils/fileTypes";
import { Typography } from "@mui/material";
import { truncateText } from "../../../utils/truncateText";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Parse timestamp parts without applying timezone conversion.
 * Keeps the original hour/minute/second values from API payload.
 */
function parseDateParts(dateString) {
  if (!dateString) return null;

  const rawValue = String(dateString).trim();
  const timezoneStripped = rawValue
    .replace(/[zZ]$/, "")
    .replace(/([+-]\d{2}:?\d{2})$/, "");
  const match = timezoneStripped.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );

  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0),
    hasTime: Boolean(match[4] && match[5]),
    hasSecond: Boolean(match[6]),
  };
}

function to12HourParts(hour) {
  const hour12 = ((hour + 11) % 12) + 1;
  const meridiem = hour >= 12 ? "PM" : "AM";
  return { hour12, meridiem };
}

/**
 * Formats a date string to "MMM d, yyyy, h:mm a" format
 */
function formatDate(dateString) {
  const parts = parseDateParts(dateString);
  if (!parts) return "";

  const month = MONTHS_SHORT[parts.month - 1];
  if (!month) return "";

  if (!parts.hasTime) {
    return `${month} ${parts.day}, ${parts.year}`;
  }

  const { hour12, meridiem } = to12HourParts(parts.hour);
  const minute = String(parts.minute).padStart(2, "0");
  return `${month} ${parts.day}, ${parts.year}, ${hour12}:${minute} ${meridiem}`;
}

/**
 * Formats a date string to a more detailed full date/time format for tooltips
 */
function formatFullDate(dateString) {
  const parts = parseDateParts(dateString);
  if (!parts) return "";

  const month = MONTHS_LONG[parts.month - 1];
  if (!month) return "";

  const weekday =
    WEEKDAYS_LONG[
      new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
    ];

  if (!parts.hasTime) {
    return `${weekday}, ${month} ${parts.day}, ${parts.year}`;
  }

  const { hour12, meridiem } = to12HourParts(parts.hour);
  const minute = String(parts.minute).padStart(2, "0");
  const second = String(parts.second).padStart(2, "0");

  if (!parts.hasSecond) {
    return `${weekday}, ${month} ${parts.day}, ${parts.year}, ${hour12}:${minute} ${meridiem}`;
  }

  return `${weekday}, ${month} ${parts.day}, ${parts.year}, ${hour12}:${minute}:${second} ${meridiem}`;
}

/**
 * Truncates text to a maximum length with ellipsis
 */
// function truncateText(text, maxLength = 15) {
//   if (!text) return "";
//   if (text.length <= maxLength) return text;
//   return text.substring(0, maxLength) + "...";
// }

/**
 * Truncates text to 6 characters with ellipsis if longer
 */
function truncateToSix(text) {
  if (!text) return "";
  if (text.length <= 6) return text;
  return text.substring(0, 6) + "...";
}

/**
 * Extracts patrol ID from full ID (e.g., "PATROL-018" -> "018")
 */
function extractPatrolId(id) {
  if (!id) return "";
  const parts = id.split("-");
  return parts.length > 1 ? parts[parts.length - 1] : id;
}

/**
 * Extracts session ID from session number (e.g., 1 -> "S-001")
 */
function formatSession(sessionNumber) {
  if (!sessionNumber) return "";
  const num =
    typeof sessionNumber === "string" ? parseInt(sessionNumber) : sessionNumber;
  return `S-${String(num).padStart(3, "0")}`;
}

/**
 * Formats patrol ID with zero-padding (e.g., 1 -> "001", 10 -> "010", 789 -> "789")
 */
function formatPatrolId(patrolId) {
  if (!patrolId && patrolId !== 0) return "";
  const num = typeof patrolId === "string" ? parseInt(patrolId) : patrolId;
  return String(num).padStart(3, "0");
}

/** Directory portion of a recording path (no trailing slash). */
function getDirectoryPath(filePath) {
  if (!filePath || typeof filePath !== "string") return "";
  const idx = filePath.lastIndexOf("/");
  return idx > 0 ? filePath.slice(0, idx) : "";
}

/**
 * List view component for patrols displayed as a table
 */
export function PatrolListView({
  patrols = [],
  selectedPatrolId,
  onPatrolClick,
  onActionClick,
  columnVisibility = { id: true, path: false, action: true },
  onCopy,
  /** Background for non-selected data rows (Compare Patrol uses panel tint). */
  sessionListSurfaceColor = "white",
}) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  /**
   * Handles column header click for sorting
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  /**
   * Sorts patrols based on selected column and direction
   */
  const sortedPatrols = useMemo(() => {
    if (!sortColumn) {
      return patrols.map((patrol, index) => ({ patrol, originalIndex: index }));
    }

    const patrolsWithIndex = patrols.map((patrol, index) => ({
      patrol,
      originalIndex: index,
    }));

    return [...patrolsWithIndex].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case "id":
          aValue =
            a.patrol?.patrol_id != null ? parseInt(a.patrol.patrol_id) || 0 : 0;
          bValue =
            b.patrol?.patrol_id != null ? parseInt(b.patrol.patrol_id) || 0 : 0;
          break;
        case "date":
          aValue = a.patrol.created_at
            ? new Date(a.patrol.created_at).getTime()
            : 0;
          bValue = b.patrol.created_at
            ? new Date(b.patrol.created_at).getTime()
            : 0;
          break;
        case "path":
          aValue = getDirectoryPath(
            a.patrol?.recordings?.[0]?.path,
          ).toLowerCase();
          bValue = getDirectoryPath(
            b.patrol?.recordings?.[0]?.path,
          ).toLowerCase();
          break;
        case "name":
          const aName =
            a.patrol?.recordings &&
            a.patrol.recordings.length > 0 &&
            a.patrol.recordings[0].path
              ? a.patrol.recordings[0].path.split("/").pop()
              : "";
          const bName =
            b.patrol?.recordings &&
            b.patrol.recordings.length > 0 &&
            b.patrol.recordings[0].path
              ? b.patrol.recordings[0].path.split("/").pop()
              : "";
          aValue = aName.toLowerCase();
          bValue = bName.toLowerCase();
          break;
        case "session":
          const aSession = a.patrol?.session_name?.split("_")[1] || "";
          const bSession = b.patrol?.session_name?.split("_")[1] || "";
          aValue = parseInt(aSession) || 0;
          bValue = parseInt(bSession) || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [patrols, sortColumn, sortDirection]);

  /**
   * Renders sort icon for a column
   */
  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, color: "#212121" }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, color: "#212121" }} />
    );
  };

  const handleClick = async (text, type) => {
    // e.stopPropagation();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`, {
        duration: 2000,
        icon: "📋",
      });

      onCopy && onCopy(text);
    } catch {
      toast.error(`Failed to copy ${type}!`, { duration: 2000 });
    }
  };

  return (
    <TableContainer
      sx={{
        overflowX: "auto",
      }}
    >
      <Table
        stickyHeader
        sx={{
          width: "100%",
          minWidth: "300px",
          tableLayout: "fixed",
          "& .MuiTableCell-root": {
            borderBottom: "none",
            paddingTop: TABLE_CONFIG_2.cellPadding.vertical,
            paddingBottom: TABLE_CONFIG_2.cellPadding.vertical,
            paddingLeft: TABLE_CONFIG_2.cellPadding.horizontal,
            paddingRight: TABLE_CONFIG_2.cellPadding.horizontal,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
            padding: "12px 10px",
          },
        }}
      >
        <TableHead
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            bgcolor: "white",
            "& .MuiTableCell-root": {
              bgcolor: "white",
            },
          }}
        >
          <TableRow>
            {columnVisibility.id && (
              <TableCell
                onClick={() => handleSort("id")}
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#212121",
                  bgcolor: "white",
                  // borderBottom: "1px solid #E5E7EB",
                  py: 1.5,
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 101,
                  width: TABLE_CONFIG_2.columns.patrolId.minWidth,
                  minWidth: TABLE_CONFIG_2.columns.patrolId.minWidth,
                  maxWidth: TABLE_CONFIG_2.columns.patrolId.maxWidth,
                  "&:hover": {
                    bgcolor: "rgb(255, 224, 208)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  ID
                  {renderSortIcon("id")}
                </Box>
              </TableCell>
            )}
            <TableCell
              onClick={() => handleSort("date")}
              sx={{
                fontFamily: "Roboto, sans-serif",
                minWidth: TABLE_CONFIG_2.columns.date.minWidth,
                width: TABLE_CONFIG_2.columns.date.width,
                fontSize: "14px",
                fontWeight: 600,
                color: "#212121",
                bgcolor: "white",
                // borderBottom: "1px solid #E5E7EB",
                py: 1.5,
                cursor: "pointer",
                position: "relative",
                zIndex: 101,
                "&:hover": {
                  bgcolor: "rgb(255, 224, 208)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                Date
                {renderSortIcon("date")}
              </Box>
            </TableCell>
            {columnVisibility.path && (
              <TableCell
                onClick={() => handleSort("path")}
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#212121",
                  bgcolor: "white",
                  py: 1.5,
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 101,
                  width: TABLE_CONFIG_2.columns.path.width,
                  minWidth: 120,
                  "&:hover": {
                    bgcolor: "rgb(255, 224, 208)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  Path
                  {renderSortIcon("path")}
                </Box>
              </TableCell>
            )}
            <TableCell
              onClick={() => handleSort("name")}
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#212121",
                bgcolor: "white",
                // borderBottom: "1px solid #E5E7EB",
                py: 1.5,
                cursor: "pointer",
                position: "relative",
                zIndex: 101,
                width: TABLE_CONFIG_2.columns.name.width,
                "&:hover": {
                  bgcolor: "rgb(255, 224, 208)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                Name
                {renderSortIcon("name")}
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort("session")}
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#212121",
                bgcolor: "white",
                // borderBottom: "1px solid #E5E7EB",
                py: 1.5,
                cursor: "pointer",
                position: "relative",
                zIndex: 101,
                width: TABLE_CONFIG_2.columns.session.width,
                "&:hover": {
                  bgcolor: "rgb(255, 224, 208)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                Session
                {renderSortIcon("session")}
              </Box>
            </TableCell>
            {/* {columnVisibility.action && (
              <TableCell
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#4A5565",
                  bgcolor: "white",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5,
                  width: "60px",
                  position: "relative",
                  zIndex: 101,
                  "&:hover": {
                    bgcolor: "rgba(255, 133, 85, 0.15)",
                  },
                }}
              >
                Actions
              </TableCell>
            )} */}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPatrols.map((item, index) => {
            const patrol = item.patrol || item;
            const originalIndex =
              item.originalIndex !== undefined ? item.originalIndex : index;
            /** Match session from parent, or default-highlight first row only when none selected. */
            const isSelected =
              selectedPatrolId === patrol?.session_id ||
              (selectedPatrolId == null && index === 0);
            const patrolId = patrol?.session_id ? patrol?.session_id : "N/A";
            const patrol_Id = patrol?.id ? patrol?.id : "N/A";
            const date = formatDate(patrol.created_at);
            const fullDate = formatFullDate(patrol.created_at);
            const path = patrol?.recordings?.[0]?.path;
            const directoryPath = getDirectoryPath(path);
            const name = path ? path.split("/").pop() : "";
            const session = `S-${String(patrol?.session_name?.split("_")[1]).padStart(3, "0")}`;

            return (
              <TableRow
                key={patrol?.session_id || index}
                onClick={() =>
                  onPatrolClick?.(
                    patrol?.recordings || [],
                    patrol?.session_id,
                    patrol?.id ?? patrol?.patrol_id ?? patrol?.session_id,
                    patrol?.created_at,
                  )
                }
                sx={{
                  cursor: "pointer",
                  border: 0,
                  padding: "8px 10px",
                  bgcolor: isSelected
                    ? "rgb(255, 224, 208)"
                    : sessionListSurfaceColor,
                  "&:hover": {
                    bgcolor: "rgb(255, 224, 208)",
                  },
                }}
              >
                {columnVisibility.id && (
                  <TableCell
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      color: "#4A5565",
                      // borderBottom: "1px solid #E5E7EB",
                      py: 8,
                      width: TABLE_CONFIG_2.columns.patrolId.minWidth,
                      minWidth: TABLE_CONFIG_2.columns.patrolId.minWidth,
                      maxWidth: TABLE_CONFIG_2.columns.patrolId.maxWidth,
                    }}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ py: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "white",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Patrol ID:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Roboto Mono, monospace",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#e0e0e0",
                              wordBreak: "break-all",
                            }}
                          >
                            {patrolId || ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "11px",
                              fontWeight: 400,
                              color: "#b0b0b0",
                              display: "block",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            💡 Click to copy ID
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      enterDelay={300}
                      leaveDelay={0}
                      PopperProps={{
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            bgcolor: "rgba(33, 33, 33, 0.95)",
                            backdropFilter: "blur(8px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                            maxWidth: "400px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "rgba(33, 33, 33, 0.95)",
                          },
                        },
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(patrolId, "ID");
                        }}
                        sx={{
                          minWidth: 60,
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          borderRadius: 0.5,
                          px: 0.5,
                          py: 0.25,
                          transition: "all 0.2s ease",
                          "&:hover": true
                            ? {
                                bgcolor: "rgba(41, 50, 229, 0.04)",
                                textDecoration: true
                                  ? "underline dotted"
                                  : "none",
                                color: "#212121",
                              }
                            : {},
                          "&:focus": {
                            outline: "2px solid #2932E5",
                            outlineOffset: "2px",
                          },
                          "&:focus:not(:focus-visible)": {
                            outline: "none",
                          },
                          textUnderlineOffset: "2px",
                          transition: "color 0.2s ease",
                        }}
                      >
                        P-{patrol_Id}
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#4A5565",
                    // borderBottom: "1px solid #E5E7EB",
                    py: 4,
                    minWidth: TABLE_CONFIG_2.columns.date.minWidth,
                    width: TABLE_CONFIG_2.columns.date.width,
                  }}
                >
                  <Tooltip
                    title={fullDate || date || ""}
                    arrow
                    placement="top"
                    enterDelay={300}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "default",
                      }}
                    >
                      {date}
                    </Box>
                  </Tooltip>
                </TableCell>
                {columnVisibility.path && (
                  <TableCell
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      color: "#4A5565",
                      py: 4,
                      width: TABLE_CONFIG_2.columns.path.width,
                      minWidth: 120,
                    }}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ py: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "white",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Full Path:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Roboto Mono, monospace",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#e0e0e0",
                              wordBreak: "break-all",
                            }}
                          >
                            {directoryPath || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "11px",
                              fontWeight: 400,
                              color: "#b0b0b0",
                              display: "block",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            💡 Click to copy path
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      enterDelay={300}
                      leaveDelay={0}
                      PopperProps={{
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            bgcolor: "rgba(33, 33, 33, 0.95)",
                            backdropFilter: "blur(8px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                            maxWidth: "400px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "rgba(33, 33, 33, 0.95)",
                          },
                        },
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (directoryPath) {
                            handleClick(directoryPath, "path");
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                          cursor: directoryPath ? "pointer" : "default",
                          borderRadius: 0.5,
                          px: 0.5,
                          py: 0.25,
                          "&:hover": directoryPath
                            ? { bgcolor: "rgba(41, 50, 229, 0.04)" }
                            : {},
                        }}
                      >
                        <FolderIcon
                          sx={{
                            fontSize: 18,
                            color: "#2932e5",
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {truncateText(directoryPath || "—", 15)}
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#4A5565",
                    // borderBottom: "1px solid #E5E7EB",
                    py: 4,
                    width: TABLE_CONFIG_2.columns.name.width,
                  }}
                >
                  <Tooltip
                    title={
                      <Box sx={{ py: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "white",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Full Filename:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto Mono, monospace",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#e0e0e0",
                            wordBreak: "break-all",
                          }}
                        >
                          {name}
                        </Typography>
                        {detectFileType("", name).isImage && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "11px",
                              fontWeight: 400,
                              color: "#b0b0b0",
                              display: "block",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            💡 Click to copy filename
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="top"
                    enterDelay={300}
                    leaveDelay={0}
                    PopperProps={{
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          bgcolor: "rgba(33, 33, 33, 0.95)",
                          backdropFilter: "blur(8px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                          maxWidth: "400px",
                          p: 1.5,
                        },
                        "& .MuiTooltip-arrow": {
                          color: "rgba(33, 33, 33, 0.95)",
                        },
                      },
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(name, "name");
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        borderRadius: 0.5,
                        px: 0.5,
                        py: 0.25,
                        transition: "all 0.2s ease",
                        "&:hover": true
                          ? {
                              bgcolor:
                                detectFileType("", name).isImage &&
                                "rgba(41, 50, 229, 0.04)",
                            }
                          : {},
                        "&:focus": {
                          outline: "2px solid #2932E5",
                          outlineOffset: "2px",
                        },
                        "&:focus:not(:focus-visible)": {
                          outline: "none",
                        },
                      }}
                    >
                      {detectFileType("", name).isImage ? (
                        <ImageIcon
                          sx={{
                            fontSize: 18,
                            color: " rgb(76, 175, 80)",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <PictureAsPdfIcon
                          sx={{
                            fontSize: 18,
                            color: " #DC2626",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          "&:hover": true
                            ? {
                                textDecoration:
                                  detectFileType("", name).isImage && true
                                    ? "underline dotted"
                                    : "none",
                                color: "#212121",
                              }
                            : {},
                          textUnderlineOffset: "2px",
                          transition: "color 0.2s ease",
                        }}
                      >
                        {truncateText(name, 23, "path") || ""}
                      </Box>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#4A5565",
                    // borderBottom: "1px solid #E5E7EB",
                    py: 4,
                    width: TABLE_CONFIG_2.columns.session.width,
                  }}
                >
                  {session}
                </TableCell>
                {/* {columnVisibility.action && (
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick?.(patrol, e);
                      }}
                      sx={{
                        color: "#4A5565",
                        "&:hover": {
                          bgcolor: "#F3F4F6",
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                )} */}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
